import React, {
  useCallback,
  useEffect,
  useState,
  ReactNode,
  SyntheticEvent,
} from 'react';

import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import classNames from 'classnames';

import { useDebounce } from '../../hooks';
import { Icon } from '../../Icon';
import { useAutocompleteReducer } from '../hooks';
import { Label, Preloader } from '../common';
import { Filter } from '../types';

import { ListBox } from './ListBox';
import { TextFieldMasked } from './TextFieldMasked';
import useStyles from './AddressInput.styles';

interface AddressInputProps {
  name?: string;
  loadItems: (
    filter: Filter,
  ) => Promise<{ value: string; addressCompleteness?: number }[]>;
  label?: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  loading?: boolean;
  tooltip?: ReactNode | null;
  error?: boolean;
  helperText?: string;
  noOptionsText?: string;
  renderOption?: (option: string) => ReactNode;
  limit?: number;
  minInputValueLength?: number;
  addressCompleteness?: number;
  mask?: string;
}

const DEBOUNCE_TIMEOUT = 500;
const SHOW_RESULT_LIMIT = 10;

/**
 * AddressInput — компонент, который предоставляет поле ввода с подсказками.
 * Он поддерживает локальные и загружаемые подсказки, настраиваемое отображение вариантов и всплывающие подсказки.
 *
 * @param {Object} props - Свойства компонента.
 * @param {string} props.name - Атрибут name для поля ввода.
 * @param {string} props.label - Текст метки для поля ввода.
 * @param {string | null} props.value - Текущее значение поля ввода.
 * @param {(value: string | undefined) => void} props.onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param {() => void} [props.onBlur] - Функция обратного вызова для обработки события потери фокуса.
 * @param {string} [props.placeholder] - Текст плейсхолдера для поля ввода.
 * @param {boolean} [props.disabled=false] - Если true, поле ввода будет отключено.
 * @param {boolean} [props.required=false] - Если true, поле ввода будет отмечено как обязательное.
 * @param {boolean} [props.clearable=false] - Если true, отображает кнопку для быстрой очистки элемента.
 * @param {boolean} [props.loading=false] - Если true, поле ввода будет показывать индикатор загрузки.
 * @param {ReactNode | null} [props.tooltip=null] - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @param {boolean} [props.error=false] - Если true, поле ввода будет отмечено как содержащее ошибку.
 * @param {string | ReactNode} [props.helperText] - Вспомогательный текст, отображаемый с полем ввода.
 * @param {(option: string) => React.ReactNode} [props.renderOption] - Пользовательская функция отображения вариантов.
 * @param {number} [props.limit=50] - Максимальное количество элементов для запроса.
 * @param {number} [props.minInputValueLength=1] - Минимальная длина ввода, необходимая для вызова подсказок.
 * @param {string} [props.mask] - Маска, применяемая к полю ввода.
 * @param {number} [props.addressCompleteness=9] - Определяет полноту заполненности адреса
 *   0 — страна
 *   1 — регион
 *   3 — район
 *   4 — город
 *   5 — район города
 *   6 — населенный пункт
 *   7 — улица
 *   8 — дом
 *   9 — квартира или комната
 *  65 — планировочная структура
 *  75 — земельный участок
 *  -1 — иностранный или пустой
 * @returns {JSX.Element} - JSX компонента.
 */

export const AddressInput = (props: AddressInputProps) => {
  const {
    name,
    label,
    value,
    onChange,
    onBlur,
    placeholder,
    disabled = false,
    required = false,
    loading = false,
    tooltip,
    error = false,
    helperText,
    noOptionsText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    mask,
  } = props;

  const { items, status, hasMore, requestItems, resetItems } =
    useAutocompleteReducer<{ value: string; addressCompleteness?: number }>(
      props.loadItems,
      true,
    );

  const { classes } = useStyles();

  const options = items.map((option) => option.value);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState<boolean>(false);

  const [lastSelectedValue, setLastSelectedValue] = useState<
    string | undefined
  >(undefined);

  const isItemsLoading = isLoading || status === 'loading';
  const debouncedInputValue = useDebounce(value, DEBOUNCE_TIMEOUT) ?? '';

  const handleChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      nextValue: string | null,
      reason: string,
    ) => {
      if (reason === 'selectOption') {
        setLastSelectedValue(nextValue ?? undefined);
      }

      onChange(nextValue ?? undefined);

      resetItems();
      setFilter({ ...filter, offset: 0, query: '' });

      setOpen(false);
    },
    [onChange, filter, resetItems],
  );

  const handleInputChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      nextValue: string | null,
      reason: string,
    ) => {
      if (reason === 'reset') return;

      if (reason === 'clear') {
        setLastSelectedValue(undefined);
      }

      onChange(nextValue ?? undefined);

      if (nextValue?.replace(/_+/g, '').trim()) {
        if (!open) {
          setOpen(true);
        }
      } else {
        setOpen(false);
      }
    },
    [onChange, open],
  );

  const handleClose = useCallback(() => {
    onBlur?.();
    if (open) setOpen(false);
  }, [onBlur, open]);

  const handleBlur = useCallback(() => {
    onChange(lastSelectedValue);
  }, [onChange, lastSelectedValue]);

  const handleOpen = useCallback(() => {
    if (value?.replace(/_+/g, '').trim()) {
      setOpen(true);
    }
  }, [value]);

  const requestNextItems = () => {
    requestItems?.(filter, 'get');
    setFilter({
      ...filter,
      offset: filter.offset + filter.limit,
    });
  };

  useEffect(() => {
    if (
      !debouncedInputValue ||
      debouncedInputValue.length < minInputValueLength
    ) {
      return;
    }

    const abortRequest = requestItems?.(
      {
        ...filter,
        offset: 0,
        query: debouncedInputValue.replace(/_+/g, '').trim(),
      },
      'search',
    );
    setFilter({
      ...filter,
      offset: filter.limit,
    });
    setLoading(false);

    return () => {
      abortRequest?.();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedInputValue]);

  useEffect(() => {
    if (!open && value) {
      handleChange(
        {} as SyntheticEvent<Element, Event>,
        value,
        'select-option',
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterOptions = (optionList: string[]) =>
    optionList.slice(0, SHOW_RESULT_LIMIT);

  return (
    <FormControl
      className={classNames(classes.addressInput, 'aqa_address_input')}
    >
      {label && <Label label={label} required={required} tooltip={tooltip} />}
      <Autocomplete
        classes={{
          paper: classes.paper,
          option: classes.option,
          ...(value
            ? {}
            : {
                clearIndicator: classes.clearIndicatorHide,
              }),
        }}
        options={options}
        filterOptions={filterOptions}
        fullWidth
        noOptionsText={noOptionsText}
        renderTags={() => null}
        disabled={disabled || loading}
        clearIcon={
          <Icon name="cancel_circle" color="stroke.secondary" size="20" />
        }
        inputValue={loading ? 'Загрузка...' : value}
        onInputChange={mask ? undefined : handleInputChange}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onBlur={handleBlur}
        renderOption={(renderOptionProps, option) => (
          <li {...renderOptionProps}>
            {renderOption ? renderOption(option) : <div>{option}</div>}
          </li>
        )}
        slotProps={{
          listbox: {
            component: ListBox,
            ...{
              dataLength: options.length,
              next: requestNextItems,
              hasMore,
              loading: isItemsLoading,
              long: !!renderOption,
            },
          },
        }}
        renderInput={(params) => {
          return mask ? (
            <TextFieldMasked
              {...params}
              mask={mask}
              name={name}
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              loading={loading}
              onChange={handleInputChange as never}
            />
          ) : (
            <TextField
              {...params}
              name={name}
              classes={{ root: classes.inputRoot }}
              autoComplete="off"
              variant="outlined"
              fullWidth
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              {...(loading
                ? {
                    slotProps: {
                      input: {
                        endAdornment: <Preloader />,
                      },
                    },
                  }
                : {})}
            />
          );
        }}
      />
    </FormControl>
  );
};
